import React, { useState, useEffect, useRef } from 'react';
import {
  Users, UserPlus, UserCog, User,
  CheckCircle, Gift,
  TrendingUp, IndianRupee, Network,
  PlusCircle, Edit, ChevronDown, ChevronRight,
  Clipboard, FileOutput, FileText, Settings, CalendarClock, Palmtree,
  ClipboardCheck, CalendarDays, Receipt, Calculator, Eye, Package,
  Activity, BarChart2, Home, LogOut, X,
  ScanLine, QrCode, MonitorDot, Clock3, CalendarCheck,
} from 'lucide-react';
import { useAuth } from './AuthContext';
import { canAccessPage } from '../utils/permissions';
import './Sidebar.css';

// ─────────────────────────────────────────────────────────────────────────────
// Navigation tree — filterItems() strips anything the user can't access via
// canAccessPage(), so each role automatically gets a tailored sidebar.
//
// Member     → Organization: My Profile, My Barcode, My Shift History
//              Workforce: Holiday Schedule, View Payslips, Leave (Apply + Records)
// ClientAdmin → Organization: Organizations, Employees, My Profile
//              Workforce: full; Finance; Compliance; Admin: Leave Settings
// SuperAdmin  → everything; Admin: Dashboard, Audit, Leave Settings, Invite Users
// ─────────────────────────────────────────────────────────────────────────────
const MENU = [
  {
    id: 'organization', title: 'Organization',
    items: [
      // Admin roles — organization & employee management
      { id: 'organizations',     name: 'Organizations',     icon: Network },
      { id: 'employees',         name: 'Employees',         icon: Users },
      // SuperAdmin only — user management
      { id: 'invite-users',      name: 'Users',             icon: UserCog },
      // Member-only — own QR code and personal shift history
      { id: 'my-barcode',        name: 'My Barcode',        icon: QrCode },
      { id: 'my-shift-history',  name: 'My Attendance Logs', icon: Clock3 },
    ],
  },
  {
    id: 'workforce', title: 'Workforce',
    items: [
      {
        // Attendance — admin roles see the full kiosk suite; Member sees only "My Attendance"
        // (auto-promoted to a flat root item by filterItems when it's the only accessible sub).
        id: 'attendance', name: 'Attendance', icon: ClipboardCheck,
        sub: [
          { id: 'attendance-registry', name: 'Attendance Registry', icon: ClipboardCheck },
          { id: 'my-attendance',       name: 'My Attendance',       icon: CalendarCheck },
          { id: 'barcode-scanner',     name: 'Barcode Scanner',     icon: ScanLine },
          { id: 'shift-live-log',      name: 'Live Shift Log',      icon: MonitorDot },
          { id: 'shift-tracker',       name: 'Shift Tracker',       icon: CalendarClock },
        ],
      },
      // Holiday Schedule — top-level sibling of Attendance (visible to everyone who can access it)
      { id: 'holiday-schedule', name: 'Holiday Schedule', icon: Palmtree },
      // Payroll insights — admin roles only (hidden for Member via permissions)
      { id: 'monthly-info-service', name: 'Payroll Insights', icon: TrendingUp },
      // Analytics — management overview (admin roles only)
      { id: 'analytics', name: 'Analytics', icon: BarChart2 },
      {
        // Payroll — admins see full suite; Member only sees View Payslips (promoted)
        id: 'payroll', name: 'Payroll', icon: IndianRupee,
        sub: [
          { id: 'generate-payroll',   name: 'Process Payroll',     icon: Calculator },
          { id: 'payslip',            name: 'View Payslips',        icon: Eye },
          { id: 'adjustments',         name: 'Adjustments',          icon: Gift },
        ],
      },
      {
        // Statutory — admin roles only (hidden for Member via permissions)
        id: 'statutory', name: 'Statutory Forms', icon: Clipboard,
        sub: [
          { id: 'forms-templates', name: 'Generate Forms', icon: FileOutput },
          { id: 'view-forms',      name: 'View Forms',     icon: Eye },
        ],
      },
      {
        // Leave — Member sees Apply + Records; admin roles see all three
        id: 'leave', name: 'Leave', icon: CalendarDays,
        sub: [
          { id: 'apply-leave',         name: 'Apply Leave',         icon: PlusCircle },
          { id: 'approve-leave',       name: 'Approve Leave',       icon: CheckCircle },
          { id: 'leave-absent-record', name: 'Leave Records',       icon: FileText },
        ],
      },
      {
        // Employment — lifecycle events (resignation, termination).
        // Member sees Apply Resignation; admins see Approve + Terminate too.
        id: 'employment', name: 'Employment', icon: LogOut,
        sub: [
          { id: 'apply-resignation',   name: 'Apply Resignation',   icon: LogOut },
          { id: 'approve-resignation', name: 'Approve Resignation', icon: CheckCircle },
          { id: 'terminate-employee',  name: 'Terminate Employee',  icon: UserCog },
        ],
      },
    ],
  },
  {
    // Finance — admin roles only
    id: 'finance', title: 'Finance',
    items: [
      {
        id: 'invoices', name: 'Invoices', icon: Receipt,
        sub: [
          { id: 'generate-invoice', name: 'Create Invoice', icon: PlusCircle },
          { id: 'view-invoices',    name: 'View Invoices',  icon: Eye },
        ],
      },
      { id: 'assets', name: 'Assets', icon: Package },
    ],
  },
];

// Admin section — shown only for ClientAdmin and SuperAdmin.
// filterItems() further narrows what each role sees:
//   ClientAdmin  → Leave Settings only
//   SuperAdmin   → all four items
const ADMIN_SECTION = {
  id: 'admin', title: 'Admin',
  items: [
    { id: 'admin-dashboard',    name: 'Admin Dashboard',     icon: Settings },
    { id: 'audit-trail',        name: 'Audit Trail',         icon: Activity },
    { id: 'groups-management',  name: 'Groups',              icon: Users },
    { id: 'leave-settings',     name: 'Leave Settings',      icon: CalendarDays },
  ],
};

export default function Sidebar({ currentView, onNavigate, isMobile, isMobileSidebarOpen, setIsMobileSidebarOpen }) {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(true);
  const [open, setOpen] = useState(new Set());
  const navRef = useRef(null);

  const isAdmin = user?.roles?.includes('SuperAdmin');
  const isManager = user?.roles?.includes('ClientAdmin') || user?.roles?.includes('MultiClientAdmin');
  const sections = isAdmin || isManager ? [...MENU, ADMIN_SECTION] : MENU;

  // Auto-expand ONLY the parent of the active view; close all others.
  // This prevents sidebar clutter from accumulated open groups (e.g. Payroll,
  // Statutory, etc.) that push Leave → Apply Leave far off-screen.
  useEffect(() => {
    const newOpen = new Set();
    for (const sec of sections) {
      for (const item of sec.items) {
        if (item.sub?.some(s => s.id === currentView)) {
          newOpen.add(item.id);
          break;
        }
      }
    }
    setOpen(newOpen);
  }, [currentView]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterItems = (items) => {
    const result = [];
    items.forEach(item => {
      if (item.sub) {
        const accessibleSub = item.sub.filter(s => canAccessPage(user, s.id));
        if (accessibleSub.length === 0) return;
        if (accessibleSub.length === 1) {
          // Single subitem remaining — promote it directly, no wrapper dropdown
          result.push(accessibleSub[0]);
        } else {
          result.push({ ...item, sub: accessibleSub });
        }
      } else if (canAccessPage(user, item.id)) {
        result.push(item);
      }
    });
    return result;
  };

  const toggle = (id) => setOpen(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const active = (id) => currentView === id;
  const parentActive = (item) => item.sub?.some(s => s.id === currentView);
  const isOpen = (item) => open.has(item.id);

  const go = (id) => {
    onNavigate(id);
    if (isMobile) setIsMobileSidebarOpen(false);
  };

  const initials = (user?.username || user?.name || 'U').slice(0, 2).toUpperCase();

  const NavItems = ({ items, collp }) => (
    <>
      {items.map(item => (
        <div key={item.id}>
          <button
            className={`sb-item${active(item.id) ? ' active' : parentActive(item) ? ' parent-active' : ''}`}
            onClick={() => item.sub ? toggle(item.id) : (collp ? onNavigate(item.id) : go(item.id))}
            title={collp ? item.name : undefined}
          >
            <item.icon size={17} className="sb-ico" />
            {!collp && <span className="sb-txt">{item.name}</span>}
            {!collp && item.sub && (
              <span className="sb-chev">
                {isOpen(item) ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              </span>
            )}
            {active(item.id) && <span className="sb-bar" />}
          </button>
          {item.sub && isOpen(item) && !collp && (
            <div className="sb-sub">
              {item.sub.map(s => (
                <button
                  key={s.id}
                  className={`sb-subitem${active(s.id) ? ' active' : ''}`}
                  onClick={() => go(s.id)}
                >
                  <s.icon size={13} className="sb-ico" />
                  <span className="sb-txt">{s.name}</span>
                  {active(s.id) && <span className="sb-bar" />}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  );

  const Footer = ({ collp }) => (
    <div className="sb-footer">
      <div className="sb-avatar">{initials}</div>
      {!collp && (
        <div className="sb-user-meta">
          <span className="sb-uname">{user?.username || user?.name || 'User'}</span>
          <span className="sb-urole">{user?.roles?.[0] || 'Member'}</span>
          {user?.establishment_name && (
            <span className="sb-uorg">{user.establishment_name}</span>
          )}
        </div>
      )}
      <button className="sb-logout" onClick={logout} title="Sign out">
        <LogOut size={15} />
      </button>
    </div>
  );

  // ---- MOBILE ----
  if (isMobile) return (
    <>
      {isMobileSidebarOpen && <div className="sb-backdrop" onClick={() => setIsMobileSidebarOpen(false)} />}
      <aside className={`sb-mobile${isMobileSidebarOpen ? ' open' : ''}`}>
        <div className="sb-head">
          <div className="sb-logo-btn" onClick={() => go('dashboard')} style={{ cursor: 'pointer' }}>
            <div className="sb-mark">HR</div>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span className="sb-brand">PayrollGen</span>
              <span style={{ fontSize: '9px', color: '#e0edff', letterSpacing: '0.05em', lineHeight: 1.3, whiteSpace: 'nowrap', fontWeight: '500', opacity: 0.9 }}>An Infra Delta Solutions Company</span>
            </div>
          </div>
          <button className="sb-icon-btn" onClick={() => setIsMobileSidebarOpen(false)}>
            <X size={17} />
          </button>
        </div>
        <nav className="sb-nav">
          <button
            className={`sb-item${active('dashboard') ? ' active' : ''}`}
            onClick={() => go('dashboard')}
          >
            <Home size={17} className="sb-ico" />
            <span className="sb-txt">Home</span>
            {active('dashboard') && <span className="sb-bar" />}
          </button>
          {sections.map(sec => {
            const items = filterItems(sec.items);
            if (items.length === 0) return null;
            return (
              <div key={sec.id} className="sb-section">
                <div className="sb-sec-hdg">{sec.title}</div>
                <NavItems items={items} collp={false} />
              </div>
            );
          })}
        </nav>
        <Footer collp={false} />
      </aside>
    </>
  );

  const scrollToActive = () => {
    setTimeout(() => {
      if (navRef.current) {
        const el = navRef.current.querySelector('.sb-subitem.active') ||
                   navRef.current.querySelector('.sb-item.active') ||
                   navRef.current.querySelector('.sb-item.parent-active');
        if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 260);
  };

  // ---- DESKTOP ----
  return (
    <aside
      className={`sb-desktop${collapsed ? ' collapsed' : ''}`}
      onMouseEnter={() => { setCollapsed(false); scrollToActive(); }}
      onMouseLeave={() => setCollapsed(true)}
    >
      <div className="sb-head">
        <div
          className="sb-logo-btn"
          onClick={() => onNavigate('dashboard')}
          style={{ cursor: 'pointer' }}
          title="Go to dashboard"
        >
          <div className="sb-mark">HR</div>
          {!collapsed && (
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span className="sb-brand">PayrollGen</span>
              <span style={{ fontSize: '9px', color: '#e0edff', letterSpacing: '0.05em', lineHeight: 1.3, whiteSpace: 'nowrap', fontWeight: '500', opacity: 0.9 }}>An Infra Delta Solutions Company</span>
            </div>
          )}
        </div>
      </div>

      <nav className="sb-nav" ref={navRef}>
        <button
          className={`sb-item${active('dashboard') ? ' active' : ''}`}
          onClick={() => onNavigate('dashboard')}
          title={collapsed ? 'Home' : undefined}
        >
          <Home size={17} className="sb-ico" />
          {!collapsed && <span className="sb-txt">Home</span>}
          {active('dashboard') && <span className="sb-bar" />}
        </button>

        {sections.map(sec => {
          const items = filterItems(sec.items);
          if (items.length === 0) return null;
          return (
            <div key={sec.id} className="sb-section">
              {collapsed
                ? <div className="sb-sec-div" />
                : <div className="sb-sec-hdg">{sec.title}</div>
              }
              <NavItems items={items} collp={collapsed} />
            </div>
          );
        })}
      </nav>

      <Footer collp={collapsed} />
    </aside>
  );
}
